import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { LegalNoticeContent } from "../legal/LegalNoticeContent";

export interface LegalNoticeDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * The publisher, privacy, security and SFTPGo/open-source licence notices
 * (src/legal/LegalNoticeContent.tsx, ADR 0008 section 8), as a modal
 * rather than a server-rendered page: available to both anonymous and
 * signed-in users (the login page footer and the authenticated app
 * shell's footer both open it), and reachable directly at /legal (see
 * App.tsx's LegalRoute). LegalNoticeContent renders its own table of
 * contents with anchor links (`href="#<section id>"`) that scroll within
 * this dialog's own scroll container (`scroll="paper"`), since the target
 * headings live inside it.
 *
 * Unlike OneTimeCredentialDialog, this one is purely informational, not a
 * one-time secret: the backdrop and Escape both close it, same as any
 * ordinary MUI dialog (MUI's default `onClose` behaviour, left untouched).
 */
export function LegalNoticeDialog({ open, onClose }: LegalNoticeDialogProps) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      scroll="paper"
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
      aria-labelledby="legal-notice-title"
    >
      <DialogTitle
        id="legal-notice-title"
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1,
          bgcolor: "background.paper",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        Legal notices, privacy and security
        <IconButton aria-label="Close" onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <LegalNoticeContent />
      </DialogContent>
    </Dialog>
  );
}
